import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Container, Fade, Zoom } from "@mui/material";
import Logo from "../components/Logo";
import "../styles/global.css";

const SplashPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          clearInterval(progressInterval);
          return 100;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 200);

    const timer = setTimeout(() => {
      navigate("/login");
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [navigate]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: "#0a0e1a",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(/mightymenofdavid.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.12,
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "radial-gradient(ellipse at center, rgba(10, 14, 26, 0.6) 0%, rgba(10, 14, 26, 0.85) 100%)",
          zIndex: 0,
        },
        "@keyframes pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 0.5 },
          "50%": { transform: "scale(1.2)", opacity: 1 },
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
          "50%": { transform: "translateY(-10px) rotate(1deg)" },
        },
        "@keyframes shimmer": {
          "0%": { backgroundPosition: "-200% 0" },
          "100%": { backgroundPosition: "200% 0" },
        },
        "@keyframes glow": {
          "0%, 100%": { opacity: 0.4, transform: "scale(1)" },
          "50%": { opacity: 0.8, transform: "scale(1.05)" },
        },
      }}
    >
      {/* Decorative floating elements */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "15%",
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(165, 164, 206, 0.03)",
          animation: "float 6s ease-in-out infinite",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "15%",
          left: "10%",
          width: 150,
          height: 150,
          borderRadius: "50%",
          background: "rgba(74, 144, 217, 0.03)",
          animation: "float 8s ease-in-out infinite reverse",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "5%",
          width: 60,
          height: 60,
          borderRadius: "50%",
          background: "rgba(165, 164, 206, 0.02)",
          animation: "float 7s ease-in-out infinite 2s",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
        <Zoom in timeout={800}>
          <Box sx={{ mb: 3 }}>
            <Logo size="large" variant="full" />
          </Box>
        </Zoom>
        
        <Fade in timeout={1000}>
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="overline"
              sx={{
                color: "#a5a4ce",
                letterSpacing: "10px",
                fontSize: "0.65rem",
                fontWeight: "600",
                textTransform: "uppercase",
                mb: 2,
                display: "block",
                position: "relative",
                opacity: 0.7,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 40,
                  height: 2,
                  background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                  borderRadius: 2,
                  opacity: 0.4,
                },
              }}
            >
              Since 1998
            </Typography>
            
            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Georgia', serif",
                fontWeight: "bold",
                fontSize: { xs: "1.8rem", md: "3.5rem" },
                color: "#ffffff",
                mb: 1,
                letterSpacing: "-0.5px",
                lineHeight: 1.1,
              }}
            >
              Worldwide
              <br />
              <span style={{ 
                background: "linear-gradient(135deg, #4a90d9, #a5a4ce)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                Corporation
              </span>
            </Typography>
            
            <Box
              sx={{
                width: 60,
                height: 3,
                background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                mx: "auto",
                my: 2.5,
                borderRadius: 2,
              }}
            />
            
            <Typography
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontFamily: "'Georgia', serif",
                fontSize: { xs: "0.8rem", md: "1rem" },
                letterSpacing: "4px",
                textTransform: "uppercase",
                py: 2,
                maxWidth: 550,
                mx: "auto",
                fontWeight: "300",
              }}
            >
              A Premier Multinational Conglomerate
            </Typography>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: { xs: 2, md: 4 }, flexWrap: "wrap" }}>
              {["Energy", "Precious Metals", "Communications", "Agriculture", "Finance", "Legal", "Human Resources"].map((sector, index) => (
                <Fade in timeout={800 + index * 100} key={sector}>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.2)",
                      fontSize: "0.5rem",
                      letterSpacing: "3px",
                      textTransform: "uppercase",
                      fontWeight: "600",
                      transition: "all 0.3s ease",
                      cursor: "default",
                      "&:hover": {
                        opacity: 1,
                        color: "#4a90d9",
                      },
                    }}
                  >
                    {sector}
                  </Typography>
                </Fade>
              ))}
            </Box>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid rgba(255,255,255,0.05)",
                maxWidth: 350,
                mx: "auto",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.2)",
                  fontFamily: "'Georgia', serif",
                  fontSize: "0.55rem",
                  letterSpacing: "6px",
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Private Access Required
              </Typography>
              
              {/* Loading Progress Bar */}
              <Box
                sx={{
                  width: "100%",
                  height: 2.5,
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: `${progress}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #4a90d9, #a5a4ce)",
                    borderRadius: 2,
                    transition: "width 0.3s ease",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                      animation: "shimmer 1.5s ease-in-out infinite",
                      backgroundSize: "200% 100%",
                    },
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.15)",
                  fontSize: "0.4rem",
                  letterSpacing: "2px",
                  mt: 1,
                }}
              >
                Loading • Please wait
              </Typography>
            </Box>

            {/* 300 Mighty Men Callout - Small version on splash */}
            <Box
              sx={{
                mt: 4,
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                py: 1,
                borderRadius: 2,
                background: "rgba(255,215,0,0.05)",
                border: "1px solid rgba(255,215,0,0.08)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography sx={{ fontSize: "1.2rem", color: "#ffd700" }}>⚔️</Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "0.55rem",
                  letterSpacing: "1px",
                  fontWeight: "500",
                }}
              >
                Recruiting the 300 Mighty Men
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* Decorative corner elements */}
      <Box
        sx={{
          position: "absolute",
          top: 30,
          left: 30,
          width: 40,
          height: 40,
          border: "1px solid rgba(255,255,255,0.03)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          right: 30,
          width: 60,
          height: 60,
          border: "1px solid rgba(255,255,255,0.02)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 30,
          right: 30,
          width: 20,
          height: 20,
          border: "1px solid rgba(165, 164, 206, 0.05)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: 30,
          width: 30,
          height: 30,
          border: "1px solid rgba(165, 164, 206, 0.04)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
    </Box>
  );
};

export default SplashPage;